import { X, Play, ExternalLink } from "lucide-react";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  videoUrl: string;
  athleteName?: string;
}

export function VideoModal({ isOpen, onClose, title, videoUrl, athleteName }: VideoModalProps) {
  if (!isOpen) return null;

  // Format video url for iframe embedding (YouTube, Vimeo, etc.)
  let embedUrl = videoUrl;
  const ytMatch = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
  if (ytMatch) {
    embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
  } else {
    const vimeoMatch = videoUrl.match(/(?:vimeo\.com\/)(\d+)/i);
    if (vimeoMatch) {
      embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
  }

  const isEmbeddable = ytMatch || embedUrl.includes("vimeo.com") || embedUrl.includes("embed");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-200">
      {/* Click outside backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-full max-w-4xl bg-slate-950 border border-yellow-500/30 rounded-3xl overflow-hidden shadow-2xl z-10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-yellow-400">
              <Play className="w-4 h-4 fill-yellow-400" />
            </div>
            <div>
              <h3 className="text-white font-black text-base">{title || "Match Highlight Compilation"}</h3>
              {athleteName && <p className="text-slate-400 text-xs">{athleteName} • Scouting Reel</p>}
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video Player Frame */}
        <div className="relative aspect-video w-full bg-black flex items-center justify-center">
          {isEmbeddable ? (
            <iframe
              src={embedUrl}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
            />
          ) : (
            <div className="text-center p-8">
              <Play className="w-12 h-12 text-yellow-400 mx-auto mb-3 opacity-60" />
              <p className="text-slate-300 text-sm font-semibold mb-4">
                This video is hosted on an external scouting portal.
              </p>
              <a
                href={videoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xs uppercase tracking-wider transition-colors shadow-lg"
              >
                Open Footage in New Window <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-slate-900/60 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
          <span>Verified Eliada Sports Agency Scouting Reel</span>
          <a
            href={videoUrl}
            target="_blank"
            rel="noreferrer"
            className="text-yellow-400 hover:underline flex items-center gap-1 font-semibold"
          >
            Direct Video Link <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
